import { Search } from "lucide-react";
import { useEffect, useState } from "react";

import { Footer } from "@/components/Footer";
import { RegionCard } from "@/components/region/RegionCard";
import { CreateRegionDialog } from "@/components/region/RegionCreateDialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sidebar } from "@/components/Sidebar";

interface RegionAttributes {
  Name: string;
  Observation: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

interface RegionData {
  id: number;
  attributes: RegionAttributes;
}

interface ApiResponse {
  data: RegionData[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

interface Region {
  id: number;
  Name: string;
  Observation: string;
}

export function RegionControl() {
  const [regions, setRegions] = useState<Region[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<"name" | "observation">("name");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const itemsPerPage = 10;

  const route = import.meta.env.VITE_URL_ENDPOINT;

  const fetchRegions = async () => {
    try {
      setLoading(true);
      const searchFilter = searchTerm
        ? `&filters[${filterType === "name" ? "Name" : "Observation"}][$containsi]=${encodeURIComponent(searchTerm)}`
        : "";

      const requestUrl = `${route}/api/regions?pagination[page]=${currentPage}&pagination[pageSize]=${itemsPerPage}&sort=Name:asc${searchFilter}`;
      const response = await fetch(requestUrl);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data: ApiResponse = await response.json();
      const formattedRegions = data.data
        .map((region: RegionData) => ({
          id: region.id,
          Name: region.attributes.Name,
          Observation: region.attributes.Observation,
        }))
        .sort((a, b) => a.Name.localeCompare(b.Name));
      setRegions(formattedRegions);
      setTotalPages(data.meta.pagination.pageCount || 1);
    } catch (error) {
      console.error("Failed to fetch regions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegions();
  }, [currentPage, searchTerm, filterType]);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleFilterChange = (value: "name" | "observation") => {
    setFilterType(value);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isAdmin = true;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex min-h-screen">
        <Sidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          isAdmin={isAdmin}
        />

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <div className="flex flex-1 flex-col lg:ml-0">
          {/* Top Header */}
          <header className="border-b border-gray-200 bg-white shadow-sm">
            <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
              <div className="flex items-center">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 lg:hidden"
                >
                  <i className="fas fa-bars text-xl"></i>
                </button>
                <h1 className="ml-4 text-xl font-semibold text-gray-900 sm:text-2xl lg:ml-0">
                  Gerenciamento de Regiões
                </h1>
              </div>
            </div>
          </header>

          {/* Filtros e conteúdo */}
          <div className="flex-1 p-8">
            <div className="mx-auto max-w-4xl">
              <h1 className="mb-3 text-center text-2xl font-bold">
                Lista de Regiões
              </h1>
              {/* Filtros */}
              <div className="mb-6 flex flex-col space-y-4">
                <div className="flex space-x-4">
                  <div className="relative flex-grow">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                      <Search className="text-gray-400" />
                    </span>
                    <Input
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="w-full pl-10"
                      placeholder={`Buscar por ${
                        filterType === "name" ? "nome" : "observação"
                      } da região`}
                    />
                  </div>
                  <Select value={filterType} onValueChange={handleFilterChange}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filtrar por" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">Nome</SelectItem>
                      <SelectItem value="observation">Observação</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <CreateRegionDialog onRegionCreate={fetchRegions} />
              </div>

              {/* Lista de regiões */}
              <div className="mt-4">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">Carregando regiões...</div>
                  </div>
                ) : regions.length > 0 ? (
                  <>
                    {regions.map((region) => (
                      <RegionCard
                        key={region.id}
                        id={region.id}
                        name={region.Name}
                        observation={region.Observation}
                        onRegionUpdate={fetchRegions}
                      />
                    ))}

                    {/* Paginação */}
                    {!loading && regions.length > 0 && totalPages > 1 && (
                      <div className="mt-8 flex flex-col items-center space-y-4 sm:flex-row sm:justify-center sm:space-x-2 sm:space-y-0">
                        <button
                          onClick={handlePrevPage}
                          disabled={currentPage === 1}
                          className="flex w-full items-center justify-center space-x-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                        >
                          <i className="fas fa-chevron-left"></i>
                          <span>Anterior</span>
                        </button>

                        <div className="flex space-x-1">
                          {(() => {
                            const pages = [];
                            const maxVisiblePages = 5;
                            const halfVisible = Math.floor(maxVisiblePages / 2);

                            let startPage = Math.max(
                              1,
                              currentPage - halfVisible,
                            );
                            let endPage = Math.min(
                              totalPages,
                              startPage + maxVisiblePages - 1,
                            );

                            // Ajustar startPage se endPage estiver no limite
                            if (endPage - startPage + 1 < maxVisiblePages) {
                              startPage = Math.max(
                                1,
                                endPage - maxVisiblePages + 1,
                              );
                            }

                            // Adicionar primeira página e reticências se necessário
                            if (startPage > 1) {
                              pages.push(
                                <button
                                  key={1}
                                  onClick={() => setCurrentPage(1)}
                                  className="h-10 w-10 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                  1
                                </button>,
                              );

                              if (startPage > 2) {
                                pages.push(
                                  <span
                                    key="dots1"
                                    className="flex h-10 w-10 items-center justify-center text-gray-500"
                                  >
                                    ...
                                  </span>,
                                );
                              }
                            }

                            // Adicionar páginas visíveis
                            for (let i = startPage; i <= endPage; i++) {
                              pages.push(
                                <button
                                  key={i}
                                  onClick={() => setCurrentPage(i)}
                                  className={`h-10 w-10 rounded-lg text-sm font-medium ${
                                    currentPage === i
                                      ? "bg-blue-600 text-white"
                                      : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                                  }`}
                                >
                                  {i}
                                </button>,
                              );
                            }

                            // Adicionar reticências e última página se necessário
                            if (endPage < totalPages) {
                              if (endPage < totalPages - 1) {
                                pages.push(
                                  <span
                                    key="dots2"
                                    className="flex h-10 w-10 items-center justify-center text-gray-500"
                                  >
                                    ...
                                  </span>,
                                );
                              }

                              pages.push(
                                <button
                                  key={totalPages}
                                  onClick={() => setCurrentPage(totalPages)}
                                  className="h-10 w-10 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                  {totalPages}
                                </button>,
                              );
                            }

                            return pages;
                          })()}
                        </div>

                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className="flex w-full items-center justify-center space-x-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                        >
                          <span>Próxima</span>
                          <i className="fas fa-chevron-right"></i>
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <h2 className="mt-10 text-center">
                    Não possui região cadastrada no sistema
                  </h2>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
